import { DetailWell } from '@/components/patterns/detail-page'
import { InfoNotice } from '@/components/patterns/info-notice'
import { LoadingState } from '@/components/patterns/loading-state'
import { CashDenominationCounter } from '@/components/pos/CashDenominationCounter'
import { CashDrawerDifference } from '@/components/pos/CashDrawerReconciliation'
import { CashDrawerWorkflowStep } from '@/components/pos/CashDrawerWorkflowStep'
import { useRotatingIdempotencyKey } from '@/components/pos/use-rotating-idempotency-key'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { serverFieldErrors, submitSchemaValidator, useAppForm } from '@/lib/form'
import {
  useCurrentCashDrawer,
  useStartCashDrawerMutation,
} from '@/lib/services/cash-drawers/cash-drawers.query'
import { startCashDrawerSchema } from '@/lib/services/cash-drawers/cash-drawers.schema'
import type {
  CashDrawerSession,
  CurrentCashDrawer,
} from '@/lib/services/cash-drawers/cash-drawers.types'
import { formatTillDate } from '@/lib/services/cash-drawers/cash-drawers.utils'
import { formatCurrency, getErrorMessage, parseCurrency } from '@/lib/utils'
import { Banknote, Plus, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

export interface CashDrawerOpenWorkflowProps {
  workstationId: number
  onSuccess?: (openedSession: CashDrawerSession) => unknown
  onRefresh?: () => unknown
}

// Inline counterpart to CashDrawerCloseWorkflow; POS flows keep StartDrawerDialog.
export function CashDrawerOpenWorkflow({
  workstationId,
  onSuccess,
  onRefresh,
}: CashDrawerOpenWorkflowProps) {
  const { data, isError, isFetchedAfterMount, isFetching, refetch } = useCurrentCashDrawer(
    workstationId,
    { refetchOnMount: 'always' }
  )

  if (isError) {
    return (
      <InfoNotice
        title="We couldn’t load this cash drawer"
        tone="danger"
        action={
          <Button
            type="button"
            variant="notice"
            size="xs"
            disabled={isFetching}
            onClick={() => refetch()}
          >
            <RefreshCw className={isFetching ? 'size-4 animate-spin' : 'size-4'} />
            {isFetching ? 'Trying again…' : 'Try again'}
          </Button>
        }
      >
        Check your connection and try again.
      </InfoNotice>
    )
  }

  // The opening amount must be current; a stale value is rejected by the API.
  if (!data || !isFetchedAfterMount) {
    return <LoadingState label="Loading cash drawer…" surface="card" />
  }

  if (data.session) {
    return (
      <InfoNotice
        title="Cash drawer already started"
        tone="neutral"
        action={
          <Button
            type="button"
            variant="notice"
            size="xs"
            disabled={isFetching}
            onClick={() => Promise.all([refetch(), onRefresh?.()])}
          >
            <RefreshCw className={isFetching ? 'size-4 animate-spin' : 'size-4'} />
            Refresh register
          </Button>
        }
      >
        This register already has a cash drawer in use. Refresh the register to count it.
      </InfoNotice>
    )
  }

  return (
    <CashDrawerOpenForm
      key={`${workstationId}-${data.expected_opening_cash}`}
      workstationId={workstationId}
      expectedCash={data.expected_opening_cash}
      lastClose={data.last_close}
      onSuccess={onSuccess}
    />
  )
}

interface CashDrawerOpenFormProps {
  workstationId: number
  expectedCash: CurrentCashDrawer['expected_opening_cash']
  lastClose: CurrentCashDrawer['last_close']
  onSuccess: CashDrawerOpenWorkflowProps['onSuccess']
}

function CashDrawerOpenForm({
  workstationId,
  expectedCash,
  lastClose,
  onSuccess,
}: CashDrawerOpenFormProps) {
  const mutation = useStartCashDrawerMutation()
  const [idempotencyKey, rotateIdempotencyKey] = useRotatingIdempotencyKey()
  const [showNote, setShowNote] = useState(false)
  const form = useAppForm({
    defaultValues: {
      opening_counted_cash: '',
      note: '',
    },
    validators: {
      // The schema coerces the typed strings to numbers.
      onSubmit: submitSchemaValidator(startCashDrawerSchema),
      onSubmitAsync: async ({ value, formApi }) => {
        let openedSession: CashDrawerSession
        try {
          openedSession = await mutation.mutateAsync({
            cash_drawer_workstation_id: workstationId,
            expected_opening_cash: parseCurrency(expectedCash),
            opening_counted_cash: Number(value.opening_counted_cash),
            note: value.note.trim() || undefined,
            idempotency_key: idempotencyKey,
          })
        } catch (error) {
          toast.error('Unable to start the cash drawer', { description: getErrorMessage(error) })
          return serverFieldErrors(error)
        }

        rotateIdempotencyKey()
        toast.success('Cash drawer started')
        formApi.reset()
        await onSuccess?.(openedSession)
        return undefined
      },
    },
  })

  return (
    <form.AppForm>
      <form.Form>
        <Card size="flush" className="@container overflow-hidden">
          <CardContent className="space-y-8 px-5 py-6 @sm:px-6">
            <DetailWell className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <Typography variant="labelStrong" className="block">
                  Left in the drawer at the last count
                </Typography>
                <Typography variant="supporting" className="block">
                  {lastClose
                    ? `Last counted ${formatTillDate(lastClose.recorded_at)}${lastClose.recorded_by ? ` by ${lastClose.recorded_by.name}` : ''}`
                    : 'This register has not been counted before'}
                </Typography>
              </div>
              <Typography variant="labelStrong" className="tabular-nums">
                {formatCurrency(expectedCash)}
              </Typography>
            </DetailWell>

            <form.Subscribe selector={(state) => state.values.opening_counted_cash}>
              {(cash) => (
                <CashDrawerWorkflowStep
                  number={1}
                  title="Count the cash"
                  description="Count what is physically in the drawer. Checks start fresh with each cash drawer."
                  done={cash !== ''}
                >
                  <CashDenominationCounter
                    onChange={(amount: string) =>
                      form.setFieldValue('opening_counted_cash', amount)
                    }
                  />
                  <div className="max-w-56">
                    <form.AppField name="opening_counted_cash">
                      {(field) => (
                        <field.MoneyInput
                          label="Cash counted"
                          placeholder="0.00"
                          showFreeBadge={false}
                          formatEmptyAsZero={false}
                        />
                      )}
                    </form.AppField>
                  </div>
                  <CashDrawerDifference
                    label="Starting difference"
                    counted={cash}
                    expected={expectedCash}
                  />
                </CashDrawerWorkflowStep>
              )}
            </form.Subscribe>

            <div className="@sm:pl-9">
              {!showNote ? (
                <Button type="button" variant="ghost" size="sm" onClick={() => setShowNote(true)}>
                  <Plus className="size-4" />
                  Add a note about the starting count
                </Button>
              ) : (
                <form.AppField name="note">
                  {(field) => (
                    <field.TextareaInput
                      label="Note about the starting count (optional)"
                      placeholder="Explain a difference or leave a note for whoever counts it."
                      rows={2}
                    />
                  )}
                </form.AppField>
              )}
            </div>
          </CardContent>

          <form.Subscribe selector={(state) => state.values.opening_counted_cash}>
            {(cash) => {
              const hasCount = cash !== ''

              return (
                <div className="flex flex-col gap-4 border-t bg-muted/20 p-5 @sm:flex-row @sm:items-center @sm:justify-between @sm:px-6">
                  <div className="min-w-0 space-y-1">
                    <Typography variant="labelStrong" className="block">
                      Start the cash drawer
                    </Typography>
                    <Typography variant="supporting" className="block">
                      {hasCount
                        ? 'Cash and checks are recorded to this cash drawer until it is counted.'
                        : 'Count the cash before starting this cash drawer.'}
                    </Typography>
                  </div>
                  <form.SubmitButton
                    mutation={mutation}
                    disabled={!hasCount}
                    className="w-full shrink-0 @sm:w-auto"
                  >
                    <Banknote className="size-4" />
                    Start cash drawer
                  </form.SubmitButton>
                </div>
              )
            }}
          </form.Subscribe>
        </Card>
      </form.Form>
    </form.AppForm>
  )
}
